import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { theme } from '../styles/theme';

const PageWrapper = styled.div`
  padding: ${theme.spacing['2xl']} 0;
  min-height: calc(100vh - 200px);
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes['3xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xl};
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.xl};
  max-width: 800px;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  background: white;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const Checkbox = styled.input`
  margin-right: ${theme.spacing.sm};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  min-height: 100px;
  resize: vertical;
  transition: all ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
`;

const DangerZone = styled(Card)`
  border: 1px solid ${theme.colors.error}30;
  background: ${theme.colors.error}05;
`;

const DangerButton = styled(Button)`
  background: ${theme.colors.error};
  
  &:hover {
    background: ${theme.colors.error}dd;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  margin-bottom: ${theme.spacing.lg};
  background: ${props => props.type === 'success' ? theme.colors.success + '10' : theme.colors.error + '10'};
  color: ${props => props.type === 'success' ? theme.colors.success : theme.colors.error};
  border: 1px solid ${props => props.type === 'success' ? theme.colors.success + '30' : theme.colors.error + '30'};
`;

export const SettingsPage: React.FC = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [settings, setSettings] = useState({
    api_endpoint: 'http://localhost:8017',
    user_category: '',
    system_prompt: '',
    show_preview: true,
    auto_improve: false,
    theme: 'light',
    language: 'en'
  });

  useEffect(() => {
    if (!user) {
      navigate('/get-started');
      return;
    }
    
    // Load user settings
    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      const response = await api.auth.getSettings();
      setSettings({
        api_endpoint: response.api_endpoint || 'http://localhost:8017',
        user_category: response.user_category || '',
        system_prompt: response.system_prompt || '',
        show_preview: response.show_preview !== false,
        auto_improve: response.auto_improve === true,
        theme: response.theme || 'light',
        language: response.language || 'en'
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage(null);
    
    try {
      await api.auth.updateSettings(settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      
      // Update user context if needed
      if (user) {
        updateUser({ ...user, settings });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.auth.deleteAccount();
      await logout();
      navigate('/');
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete account. Please try again.' });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <PageWrapper>
      <Container>
        <PageTitle>Settings</PageTitle>
        
        {message && (
          <Message type={message.type}>{message.text}</Message>
        )}
        
        <SettingsGrid>
          <Card>
            <SectionTitle>General Settings</SectionTitle>
            
            <FormGroup>
              <Label>API Endpoint</Label>
              <Input
                type="text"
                value={settings.api_endpoint}
                onChange={(e) => setSettings({ ...settings, api_endpoint: e.target.value })}
                placeholder="http://localhost:8017"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>User Category</Label>
              <Select
                value={settings.user_category}
                onChange={(e) => setSettings({ ...settings, user_category: e.target.value })}
              >
                <option value="">Select a category...</option>
                <option value="developer">Developer</option>
                <option value="marketer">Marketer</option>
                <option value="writer">Writer</option>
                <option value="researcher">Researcher</option>
                <option value="student">Student</option>
                <option value="other">Other</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Custom System Prompt</Label>
              <TextArea
                value={settings.system_prompt}
                onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
                placeholder="Enter a custom system prompt to personalize AI responses..."
              />
            </FormGroup>
          </Card>
          
          <Card>
            <SectionTitle>Preferences</SectionTitle>
            
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={settings.show_preview}
                  onChange={(e) => setSettings({ ...settings, show_preview: e.target.checked })}
                />
                Show preview of improved prompts
              </CheckboxLabel>
            </FormGroup>
            
            <FormGroup>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={settings.auto_improve}
                  onChange={(e) => setSettings({ ...settings, auto_improve: e.target.checked })}
                />
                Automatically improve prompts on paste
              </CheckboxLabel>
            </FormGroup>
            
            <FormGroup>
              <Label>Theme</Label>
              <Select
                value={settings.theme}
                onChange={(e) => setSettings({ ...settings, theme: e.target.value })}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto (System)</option>
              </Select>
            </FormGroup>
            
            <FormGroup>
              <Label>Language</Label>
              <Select
                value={settings.language}
                onChange={(e) => setSettings({ ...settings, language: e.target.value })}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="ja">Japanese</option>
                <option value="zh">Chinese</option>
              </Select>
            </FormGroup>
          </Card>
          
          <ButtonGroup>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/dashboard')}>
              Cancel
            </Button>
          </ButtonGroup>
          
          <DangerZone>
            <SectionTitle>Danger Zone</SectionTitle>
            <p style={{ color: theme.colors.text.secondary, marginBottom: theme.spacing.md }}>
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <DangerButton onClick={handleDeleteAccount} size="small">
              Delete Account
            </DangerButton>
          </DangerZone>
        </SettingsGrid>
      </Container>
    </PageWrapper>
  );
};